import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getSignupProductByKey, type DistrictAdminProductKey } from '../../../config/SignupProductConfig';
import { prepareSignupContext, signInExistingAccount, startGoogleSignup, submitEmailSignup } from '../../../services/SignupService';
import { clearStoredSignupFlow, createSignupHandoffId, getSignupReturnPath, writeStoredSignupFlow, type StoredSignupFlow } from '../../../services/SignupFlowStorage';
import { isValidEmailAddress } from '../../../services/Utilities';
import { SignupAccountMethods } from '../SignupAccountMethods/SignupAccountMethods';
import { SignupHeader } from '../SignupHeader/SignupHeader';
import { SignupDomainError } from '../SignupDomainError/SignupDomainError';
import { SignupAccountExists } from '../SignupAccountExists/SignupAccountExists';
import { consentErrorMessage, emailErrorMessage, getSignupErrorMessage, type SignupActionError } from '../SignupMessages';
import { signupDistrictAdminStyles } from './SignupDistrictAdmin.styles';

interface SignupDistrictAdminProps {
  productKey: DistrictAdminProductKey;
  restoredFlow?: StoredSignupFlow;
  result?: 'domain-error' | 'account-exists';
  isArchived?: boolean;
}

export function SignupDistrictAdmin({productKey, restoredFlow, result, isArchived = false}: SignupDistrictAdminProps) {
  const theme = useTheme();
  const product = getSignupProductByKey(productKey);
  const [signupResult, setSignupResult] = useState(result);
  const [signupReturnPath] = useState(() => restoredFlow?.signupReturnPath || getSignupReturnPath(`?plan=${product.urlPlan}&view=district-admin`));
  const [emailAddress, setEmailAddress] = useState(restoredFlow?.emailAddress || '');
  const [password, setPassword] = useState('');
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(restoredFlow?.hasAcceptedTerms === true);
  const [showConsentError, setShowConsentError] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [actionError, setActionError] = useState<SignupActionError | null>(null);

  useEffect(() => {
    if (restoredFlow) {
      clearStoredSignupFlow();
    }
  }, [restoredFlow]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (!searchParams.get('token')?.trim()) {
      return;
    }

    // District access comes from the invitation; tokens must never select a teacher subscription here.
    searchParams.delete('token');
    const remainingQuery = searchParams.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}${window.location.hash}`);
  }, []);

  function handleTermsChange(checked: boolean) {
    setHasAcceptedTerms(checked);
    if (checked) {
      setShowConsentError(false);
      if (actionError?.reason === 'consent') {
        setActionError(null);
        setIsWorking(false);
      }
    }
  }

  async function handleSignup(method: 'email' | 'google') {
    if (!hasAcceptedTerms) {
      setShowConsentError(true);
      setActionError({reason: 'consent', message: consentErrorMessage});
      setIsWorking(false);
      return;
    }
    if (method === 'email' && !isValidEmailAddress(emailAddress)) {
      setActionError({reason: 'email', message: emailErrorMessage});
      setIsWorking(false);
      return;
    }
    setIsWorking(true);
    setActionError(null);

    try {
      await prepareSignupContext({selection: {type: 'eps_district_admin', productKey}});
      const handoffId = createSignupHandoffId();
      writeStoredSignupFlow({
        handoffId,
        subject: '',
        zipCode: '',
        campuses: [],
        campusId: '',
        isMissingSchool: false,
        stateOptions: [],
        stateCode: '',
        emailAddress: emailAddress.trim(),
        signupMethod: method,
        districtAdminProductKey: productKey,
        signupReturnPath,
        hasAcceptedTerms,
      });
      if (method === 'google') {
        await startGoogleSignup(handoffId);
      } else {
        submitEmailSignup(emailAddress.trim(), handoffId);
      }
    } catch (error) {
      setActionError({reason: 'request', message: getSignupErrorMessage(error)});
      setIsWorking(false);
    }
  }

  async function handleSignIn() {
    setIsWorking(true);
    setActionError(null);
    try {
      await signInExistingAccount(emailAddress.trim(), password);
      clearStoredSignupFlow();
    } catch {
      setActionError({reason: 'sign-in', message: 'We couldn’t sign you in with that password. Check your password and try again.'});
      setIsWorking(false);
    }
  }

  function showSignupMethods() {
    setSignupResult(undefined);
    setEmailAddress('');
    setPassword('');
    setActionError(null);
    setIsWorking(false);
    window.history.replaceState({}, '', signupReturnPath);
  }

  return (
    <Box component="main" sx={signupDistrictAdminStyles.page(theme)}>
      <Box sx={signupDistrictAdminStyles.content(theme)}>
        <SignupHeader
          title={`Create your ${product.label} district administrator account`}
          description="Sign up with the email address associated with your district administrator invitation."
          logoSrc={product.logoAssetUrl}
          logoAlt={`${product.label} district administrator`}
          isProductLogo
          isCentered
        />
        <Box component="form" onSubmit={(event) => { event.preventDefault(); void (signupResult === 'account-exists' ? handleSignIn() : handleSignup('email')); }} sx={signupDistrictAdminStyles.form()}>
          <Box sx={signupDistrictAdminStyles.productForm()}>
            {signupResult === 'domain-error' ? (
              <SignupDomainError inline={{
                provider: restoredFlow?.signupMethod === 'google' ? 'google' : 'email',
                schoolName: '',
                isDistrictAdmin: true,
                emailAddress,
                failedEmailAddress: restoredFlow?.emailAddress || '',
                isWorking,
                error: actionError?.message || '',
                onEmailChange: setEmailAddress,
                onUseDifferentMethod: showSignupMethods,
              }} />
            ) : signupResult === 'account-exists' ? (
              <SignupAccountExists isArchived={isArchived} inline={{
                programName: product.label,
                schoolName: '',
                password,
                isWorking,
                error: actionError?.message || '',
                onPasswordChange: setPassword,
                onUseDifferentAccount: showSignupMethods,
              }} />
            ) : <SignupAccountMethods
              isProductView
              canAttemptSignup={!isWorking}
              isWorking={isWorking}
              emailAddress={emailAddress}
              hasAcceptedTerms={hasAcceptedTerms}
              showConsentError={showConsentError}
              showEmailError={actionError?.reason === 'email'}
              actionError={actionError?.message || ''}
              onEmailChange={setEmailAddress}
              onTermsChange={handleTermsChange}
              onProviderSignUp={(provider) => {
                if (provider === 'google') {
                  void handleSignup('google');
                }
              }}
            />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
